import {getTestProps, type DataTestAttrs} from '@benchling/js-test-utils';
import {Card, Group, Text, createStyles, Flex, Badge, Box, Skeleton} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {isSimpleClick, navigate} from 'BenchlingUtil';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';
import {callOnEnter} from 'util/Keyboard';
import {ElementMouseEvent} from 'util/Types';

import {DEFAULT_BADGE_PROPS, LOADING_SKELETON_ROW_COUNT} from './HomepageConstants';

const ROW_HEIGHT = '38px';
const ROW_PADDING = '6px 16px';

const useStyles = createStyles((theme) => {
  const rowDivider = {borderTop: `1px solid ${theme.colors.gray[2]}`};

  return {
    clickable: {
      cursor: 'pointer',
    },
    flexGrow: {
      flexGrow: 1,
    },
    flexStart: {
      alignSelf: 'flex-start',
    },
    cardSectionRowHover: {
      '&:hover': {
        backgroundColor: theme.colors.gray[1],
      },
    },
    cardSectionList: {
      '& > * + *': rowDivider,
      '&:not(:first-child) > :first-child': rowDivider,
    },
    loadingRow: {
      height: ROW_HEIGHT,
      padding: ROW_PADDING,

      '&:not(:first-of-type)': {
        ...rowDivider,
      },
    },
    linkReset: {
      display: 'block',
      '&, &:hover, &:visited, &:active, &:focus': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
    cardSectionRowIcon: {
      '&&': {
        width: '16px',
        height: '16px',
      },
      marginRight: theme.spacing.sm,
    },
    cardSectionMessageRowClickable: {
      ...rowDivider,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    badgeInner: {
      fontWeight: 600,
    },
  };
});

const useCardSectionStyles = createStyles((theme, {cardContentHeight}: {cardContentHeight?: number}) => ({
  card: {
    transition: 'height 0.25s ease-out',
    height: cardContentHeight ?? 'auto',
  },
}));

/** Card layout primitive for homepage sections (title, optional header actions, bordered body). */
export default function CardSection({
  title,
  rightContent,
  children,
  dataTestAttrs,
}: {
  title: React.ReactNode;
  rightContent?: React.ReactNode;
  children: React.ReactNode;
  dataTestAttrs: DataTestAttrs;
}): JSX.Element {
  const {ref: cardContentRef, height: cardContentHeight} = useResizeObserver();
  const {classes} = useCardSectionStyles({cardContentHeight});
  const titleId = useId();

  return (
    <div role="region" aria-labelledby={titleId} {...getTestProps(dataTestAttrs)}>
      <Group position="apart" p="12px 0">
        <Text
          size="xl"
          id={titleId}
          {...getTestProps({component: dataTestAttrs.component, element: 'title'})}
        >
          {title}
        </Text>
        {rightContent}
      </Group>
      <Card radius="lg" withBorder={true} p="0" className={classes.card}>
        <div ref={cardContentRef}>{children}</div>
      </Card>
    </div>
  );
}

export interface CardSectionRowProps {
  id: string;
  name: string;
  modifiedAt?: string;
  icon?: BenchlingIconName;
  count?: number;
  href?: string;
  onClick?: () => void;
  rightContent?: React.ReactNode;
}

function CardSectionRowContent({
  icon,
  name,
  modifiedAt,
  count,
  rightContent,
}: Pick<CardSectionRowProps, 'icon' | 'name' | 'modifiedAt' | 'count' | 'rightContent'>): JSX.Element {
  const {classes} = useStyles();

  return (
    <Flex align="center" p={ROW_PADDING} mih={ROW_HEIGHT}>
      {icon && <BenchlingIcon icon={icon} className={classes.cardSectionRowIcon} />}
      <Group>
        <Text fz="lg">{name}</Text>
        {modifiedAt != null && (
          <Text color="dimmed" fz="sm">
            Updated {fuzzydate(modifiedAt, {showTime: false})}
          </Text>
        )}
      </Group>
      <div className={classes.flexGrow} />
      <Text color="dimmed" ta="right">
        {rightContent}
      </Text>
      {count != null ? (
        <Badge {...DEFAULT_BADGE_PROPS} ml="md" classNames={{inner: classes.badgeInner}}>
          {count}
        </Badge>
      ) : null}
    </Flex>
  );
}

export function CardSectionRow({
  icon,
  name,
  modifiedAt,
  count,
  href,
  onClick,
  rightContent,
}: CardSectionRowProps): JSX.Element {
  const {classes, cx} = useStyles();

  const rowContent = (
    <CardSectionRowContent
      icon={icon}
      name={name}
      modifiedAt={modifiedAt}
      count={count}
      rightContent={rightContent}
    />
  );

  let rowBody: React.ReactNode = rowContent;
  if (href != null || onClick != null) {
    rowBody = (
      <Box
        component={href != null ? 'a' : 'div'}
        href={href}
        className={cx(classes.linkReset, classes.clickable)}
        role="link"
        tabIndex={0}
        onClick={(e: ElementMouseEvent) => {
          if (href != null) {
            navigate(e, href);
            if (isSimpleClick(e)) {
              onClick?.();
            }
          } else {
            onClick?.();
          }
        }}
        onKeyDown={
          href == null && onClick != null
            ? callOnEnter(() => {
                onClick();
              })
            : undefined
        }
      >
        {rowContent}
      </Box>
    );
  }

  return (
    <Box role="listitem" className={classes.cardSectionRowHover}>
      {rowBody}
    </Box>
  );
}

/** Renders {@link CardSectionRow} items inside a `role="list"` container. */
export function CardSectionRowList({rows}: {rows: Array<CardSectionRowProps>}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Box role="list" className={classes.cardSectionList}>
      {rows.map((row) => (
        <CardSectionRow key={row.id} {...row} />
      ))}
    </Box>
  );
}

export function CardSectionMessageRow({
  message,
  onClick,
  dataTestAttrs,
}: {
  message: string | React.ReactNode;
  onClick?: () => void;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Box
      className={cx(onClick && classes.clickable, onClick && classes.cardSectionMessageRowClickable)}
      {...(onClick && getActionButtonRoleProps({onPress: onClick}))}
      {...getTestProps(dataTestAttrs ?? {})}
    >
      <Flex align="center" p={ROW_PADDING}>
        <Text color={onClick ? 'blue' : 'dimmed'} fz="lg">
          {message}
        </Text>
      </Flex>
    </Box>
  );
}

export function CardSectionLoadingRows({count}: {count: number}): JSX.Element {
  const {classes} = useStyles();
  return (
    <>
      {Array.from({length: count}).map((_, index) => (
        <Box
          key={`loading-${index}`}
          className={classes.loadingRow}
          {...getTestProps({component: 'CardSection', element: 'loadingRow', key: String(index)})}
        >
          <Skeleton height="100%" width="100%" radius="md" />
        </Box>
      ))}
    </>
  );
}

/** Renders a {@link CardSectionMessageRow} for loading or error query states; otherwise `null`. */
export function renderQueryLoadingOrError(
  queryResult: {type: QueryResultType},
  errorMessage: string,
  dataTestComponent: string
): JSX.Element | null {
  if (queryResult.type === QueryResultType.PENDING) {
    return <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <CardSectionMessageRow
        message={errorMessage}
        dataTestAttrs={{component: dataTestComponent, element: 'messageRow', key: 'error'}}
      />
    );
  }
  return null;
}
